import { AxiosResponse } from 'axios';
import { api } from 'src/common/utils';

import {
  BulkCreatePayload,
  BulkCreateResult,
  CitasUser,
  CreateCodePayload,
  InviteCode,
  InviteFromWaitlistResult,
  InviteRedemption,
  Listing,
  ListingProvider,
  Paginated,
  WaitlistEntry,
} from './types';

// Each resource is its own top-level route on cosmos_router, alongside the
// other admin-portal endpoints (account_admin, twilio_admin…) — there is no
// citas prefix to share.
const CODES = '/invite_code_admin';
const REDEMPTIONS = '/invite_redemption_admin';
const WAITLIST = '/waitlist_admin';
const LISTINGS = '/citas_listing_admin';
const USERS = '/citas_user_admin';

type CodeFilters = { page: number; search?: string; status?: string };
// Redemptions can be narrowed by the code's id (canonical) or its printed
// string; the codes table expands a row it already has the id for.
type RedemptionFilters = { page: number; search?: string; invite_code?: string; code?: string };
type WaitlistFilters = { page: number; search?: string; status?: string };
type ListingFilters = { page: number; search?: string; visibility?: string };
type UserFilters = { page: number; search?: string };

const buildQuery = (params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

export const fetchInviteCodes = (
  filters: CodeFilters
): Promise<AxiosResponse<Paginated<InviteCode>>> => api.get(`${CODES}${buildQuery(filters)}`);

export const createInviteCode = (payload: CreateCodePayload): Promise<AxiosResponse<InviteCode>> =>
  api.post(CODES, payload);

export const bulkCreateInviteCodes = (
  payload: BulkCreatePayload
): Promise<AxiosResponse<BulkCreateResult>> => api.post(`${CODES}/bulk`, payload);

// Revoking is its own action, not a PATCH of `status`: the server owns the
// transition (and keeps it idempotent), the client only asks for it.
export const revokeInviteCode = (id: string): Promise<AxiosResponse<InviteCode>> =>
  api.post(`${CODES}/${id}/revoke`);

export const fetchRedemptions = (
  filters: RedemptionFilters
): Promise<AxiosResponse<Paginated<InviteRedemption>>> =>
  api.get(`${REDEMPTIONS}${buildQuery(filters)}`);

export const fetchWaitlist = (
  filters: WaitlistFilters
): Promise<AxiosResponse<Paginated<WaitlistEntry>>> => api.get(`${WAITLIST}${buildQuery(filters)}`);

export const inviteFromWaitlist = (
  id: string,
  payload: { max_uses?: number; expires_at?: string | null }
): Promise<AxiosResponse<InviteFromWaitlistResult>> =>
  api.post(`${WAITLIST}/${id}/invite`, payload);

export const fetchListings = (
  filters: ListingFilters
): Promise<AxiosResponse<Paginated<Listing>>> => api.get(`${LISTINGS}${buildQuery(filters)}`);

export const setOfficeVisibility = (
  accountId: string,
  showListing: boolean
): Promise<AxiosResponse<Listing>> =>
  api.patch(`${LISTINGS}/${accountId}`, { show_listing: showListing });

export const setProviderVisibility = (
  accountId: string,
  providerId: string,
  isActive: boolean
): Promise<AxiosResponse<ListingProvider>> =>
  api.patch(`${LISTINGS}/${accountId}/providers/${providerId}`, { is_active: isActive });

export const fetchUsers = (filters: UserFilters): Promise<AxiosResponse<Paginated<CitasUser>>> =>
  api.get(`${USERS}${buildQuery(filters)}`);
